import { Property } from "@/features/survey/types";

interface MapPopupContentProps {
  property: Property;
  surveyId: string;
  imageSrc?: string; // Optional pre-resolved image URL
  showDetails?: boolean;
  address2Visible?: boolean;
  cityVisible?: boolean;
  stateVisible?: boolean;
  postalCodeVisible?: boolean;
}

/**
 * Creates a popup DOM element for a property marker.
 * Uses DOM APIs exclusively to avoid any XSS risk from HTML string injection.
 */
export const createMapPopupElement = ({
  property,
  surveyId,
  imageSrc,
  showDetails = true,
  address2Visible = false,
  cityVisible = true,
  stateVisible = true,
  postalCodeVisible = true,
}: MapPopupContentProps): HTMLElement => {
  const container = document.createElement("div");
  container.className = "popup-container";
  container.style.position = "relative";

  // Custom close button
  const closeBtn = document.createElement("button");
  closeBtn.className = "popup-close-btn";
  closeBtn.setAttribute("aria-label", "Close popup");
  closeBtn.textContent = "\u2715";
  closeBtn.style.cssText =
    "position:absolute;top:4px;right:6px;z-index:10;background:transparent;border:0;outline:none;box-shadow:none;cursor:pointer;color:#374151;font-size:1rem;line-height:1;padding:0;appearance:none;";
  container.appendChild(closeBtn);

  // Background image — validate URL before applying to style
  const bg = document.createElement("div");
  bg.className = "popup-bg";
  const imageUrl = imageSrc || "/image-placeholder-medium.png";
  bg.style.backgroundImage = `url(${imageUrl})`;
  container.appendChild(bg);

  const content = document.createElement("div");
  content.className = "popup-content";

  const h6 = document.createElement("h6");
  h6.textContent = (property.address1 || "").replace(/,+\s*$/, "");
  content.appendChild(h6);

  const p = document.createElement("p");
  const cityState = [
    cityVisible ? property.city : null,
    stateVisible ? property.stateProvince : null,
  ]
    .filter(Boolean)
    .join(", ");
  const address2 = address2Visible ? property.address2 : null;
  const postalCode = postalCodeVisible ? property.postalCode : null;
  const formattedAddress2 =
    address2 && address2 !== "—"
      ? cityState || postalCode
        ? `${address2},`
        : address2
      : null;
  p.textContent = [formattedAddress2, cityState || null, postalCode]
    .filter(Boolean)
    .join(" ");
  content.appendChild(p);

  if (showDetails) {
    const button = document.createElement("button");
    button.className = "popup-button";
    button.textContent = "See Details";
    button.addEventListener("click", () => {
      // cycode:ignore - surveyId and property.id are encoded via encodeURIComponent;
      // URL constructor ensures only the pathname (never a protocol/host) is passed to assign.
      const safeSurveyId = encodeURIComponent(surveyId);
      const safePropertyId = encodeURIComponent(property.id);
      const path = `/interactive/${safeSurveyId}/property/${safePropertyId}`;
      // Use URL constructor to resolve and extract only the pathname,
      // preventing any open-redirect via protocol or host injection
      const resolved = new URL(path, window.location.origin);
      window.location.assign(resolved.pathname);
    });
    content.appendChild(button);
  }

  container.appendChild(content);
  return container;
};
